// Static blog content for Providence Partners caregiver tips.
// Add a new post by appending an object to POSTS — slug must be unique.

export const POSTS = [
  {
    slug: "adult-day-care-michigan-guide",
    title: "Adult Day Care in Michigan: A 2026 Guide for Working Caregivers",
    excerpt:
      "What adult day care actually covers, what it costs across Michigan, and how to try one before you commit your parent to it full-time.",
    date: "2026-02-01",
    author: "Providence Partners Advisors",
    readMinutes: 6,
    tags: ["Adult Day Care", "Michigan", "Working Caregivers"],
    body: `
<p>If you're an adult daughter or son who recently realized Mom can't be home alone all day anymore — but you can't quit your job — adult day care is one of the most underused options in Michigan. Here's a plain-English guide to what it is, what it costs, and how to try one before you commit.</p>

<h2>What adult day care actually is</h2>
<p>Adult day care is a structured daytime program where your loved one is supervised, fed, socially engaged, and (in many programs) medically monitored — for as little as 4 hours or as long as 11 hours a day. They come home at night. You go to work. You both keep your routine.</p>
<p>Across Michigan, programs range from small adult foster care homes hosting 6–8 guests to full memory care day centers with 30+ participants, secured walking gardens, and on-site nurses.</p>

<h2>What it costs</h2>
<ul>
  <li><strong>$85–$135/day</strong> — adult foster care homes and group day programs (Warren, Dearborn, Sterling Heights)</li>
  <li><strong>$95–$160/day</strong> — small memory care cottages (Royal Oak, Birmingham)</li>
  <li><strong>$175–$220/day</strong> — dedicated memory care communities with secured units (Farmington Hills, Bloomfield Hills)</li>
</ul>
<p>Many Michigan adult day programs also accept Medicaid Waiver and long-term care insurance — ask before assuming you'll pay out of pocket.</p>

<h2>The "try before you commit" approach</h2>
<p>You don't have to enroll Mom in a permanent program. Start with a single drop-in day — most member homes in our network accept same-day drop-ins. Watch what happens at lunch. Notice how the staff greet her. See if she's tired or energized when you pick her up.</p>
<p>If it's a fit, build up: one Tuesday, then two days a week, then a respite weekend. By the time you're considering placement, you already know the home.</p>

<h2>How to find one near you</h2>
<p>Use the <a href="/search">Providence Partners locator</a> to filter member homes by city, ZIP, and care priority (memory care, Parkinson's, mobility). Every facility is editable only by its own team and re-verified annually.</p>

<p>Or just call <strong>(586) 306-8449</strong>. We'll match you to 2–3 vetted options near you — always available, 24/7.</p>
    `,
  },
  {
    slug: "memory-care-vs-assisted-living-michigan",
    title: "Memory Care vs. Assisted Living in Michigan: Which One Is Right?",
    excerpt:
      "Both look the same in a brochure. They are not the same. Here's how to tell which setting fits your loved one's actual diagnosis and stage.",
    date: "2026-02-08",
    author: "Providence Partners Advisors",
    readMinutes: 5,
    tags: ["Memory Care", "Assisted Living", "Placement"],
    body: `
<p>The first time most families tour a senior community, they're handed a brochure that lists both "assisted living" and "memory care." Both look beautiful. Both sound the same. They are not. Choosing wrong costs families thousands of dollars and — sometimes — a heartbreaking second move six months later.</p>

<h2>Assisted living</h2>
<p>Designed for adults who need help with daily living — bathing, medication reminders, meal preparation — but who are <em>cognitively intact</em>. They can navigate their day, recognize family, follow a schedule, and remember meals. They thrive in a community with social activities, restaurant-style dining, and as much independence as possible.</p>

<h2>Memory care</h2>
<p>Designed specifically for adults with dementia, Alzheimer's, or other cognitive decline. The physical layout is different (often a secured unit to prevent wandering). The activities are different (life-skill stations like folding laundry or sorting fabric, music therapy, sensory gardens). The staff training is different (dementia-specific communication techniques, redirection, gentle reorientation).</p>

<h2>Signs your loved one needs memory care, not assisted living</h2>
<ul>
  <li>They wander — or have left the house at night and didn't know where they were going</li>
  <li>They no longer recognize family members consistently</li>
  <li>Medication is missed or doubled up despite reminders</li>
  <li>They become agitated, fearful, or aggressive — especially at sundown</li>
  <li>They can't follow a 2-step instruction reliably</li>
</ul>

<h2>"Secured memory care" — the next step</h2>
<p>Some memory care communities offer a <strong>secured</strong> unit (locked exterior doors with keypad release) for residents who are active wanderers. In our locator we tag these homes with a <em>Secured Memory Care</em> specialty so you can filter for them directly.</p>

<h2>How to choose without guessing</h2>
<p>Call <strong>(586) 306-8449</strong>. A Providence Partners advisor will walk through your loved one's specific situation and tell you honestly whether they need assisted living or memory care — and which Michigan homes specialize in each.</p>
    `,
  },
  {
    slug: "senior-placement-oakland-county",
    title: "Senior Placement in Oakland County: A Caregiver's Step-by-Step Guide",
    excerpt:
      "From the first phone call to move-in day: how senior placement actually works in Oakland County, what it costs the family (spoiler: usually nothing), and how to avoid placement-mill agencies.",
    date: "2026-02-15",
    author: "Providence Partners Advisors",
    readMinutes: 7,
    tags: ["Senior Placement", "Oakland County", "Memory Care"],
    body: `
<p>If you're typing "senior placement Oakland County" into Google at 11pm because Dad fell again, take a breath. Here's how this actually works.</p>

<h2>What a placement advisor does (and doesn't do)</h2>
<p>A good placement advisor:</p>
<ul>
  <li>Listens to your loved one's diagnosis, medications, mobility, behavior patterns, and budget</li>
  <li>Matches you to 2–3 vetted homes — not a list of 20</li>
  <li>Coordinates the tour, sits in if you want, and translates the contracts in plain English</li>
  <li>Follows up after move-in to make sure the fit holds</li>
</ul>
<p>A placement <em>mill</em> just spams your contact info to every facility that will pay them. You'll get 15 sales calls in 48 hours and zero guidance.</p>

<h2>Does it cost the family anything?</h2>
<p>In most cases, <strong>no</strong>. Placement advisors are paid by the receiving facility — not by the family — once a placement is made. Always ask up front: "Is this service free to me?" and "Are you contracted with every facility, or only some?"</p>

<h2>What Oakland County options look like</h2>
<p>From Royal Oak and Birmingham to Farmington Hills, Bloomfield Hills, and Clarkston, Oakland County has one of the densest senior care landscapes in Michigan. We work with:</p>
<ul>
  <li><strong>Adult foster care homes</strong> — 4–8 residents, home-like setting, ~$3,500–$5,500/month</li>
  <li><strong>Memory care communities</strong> — secured, dementia-trained staff, ~$5,500–$8,500/month</li>
  <li><strong>Assisted living communities</strong> — for cognitively intact seniors, ~$4,000–$6,500/month</li>
  <li><strong>Group homes</strong> — intimate 6:1 ratios, often the best fit for late-stage memory care</li>
</ul>

<h2>Step-by-step: from call to move-in</h2>
<ol>
  <li><strong>Call (586) 306-8449.</strong> An advisor takes a short intake.</li>
  <li><strong>Trial visit.</strong> Most families start with a drop-in or respite stay first.</li>
  <li><strong>Tour 2–3 vetted options.</strong> We come with you if helpful.</li>
  <li><strong>Decision &amp; paperwork.</strong> We translate every line.</li>
  <li><strong>Move-in support.</strong> We follow up at 1 week, 1 month, and 3 months.</li>
</ol>

<p>Ready when you are. Always available — 24/7.</p>
    `,
  },
  {
    slug: "respite-care-without-guilt",
    title: "Respite Care for Family Caregivers: How to Take a Break Without the Guilt",
    excerpt:
      "If you can't remember the last time you slept eight hours, this one's for you. A practical guide to using respite care — without feeling like you're abandoning your parent.",
    date: "2026-02-22",
    author: "Providence Partners Advisors",
    readMinutes: 5,
    tags: ["Respite", "Caregiver Burnout", "Self-Care"],
    body: `
<p>You haven't been to the dentist in 18 months. You cry in your car. You snap at your kids. You haven't slept eight hours since Mom moved in. That's not a moral failure — that's caregiver burnout, and there's a fix designed exactly for this.</p>

<h2>What respite care is</h2>
<p>Respite care is short-stay care for your loved one in a vetted home or facility — anywhere from one weekend to three weeks — while you rest, travel, recover from surgery, or just <em>breathe</em>.</p>

<h2>Three types of respite, three life-savers</h2>
<ul>
  <li><strong>Weekly respite</strong> — your loved one spends one day a week (often 8–6pm) at an adult day program. You get a guaranteed workday or rest day every week.</li>
  <li><strong>Weekend respite</strong> — 2–3 nights at a member home so you can sleep, see family, or attend a wedding.</li>
  <li><strong>Long-term respite</strong> — 1–3 weeks for a real vacation, a medical recovery, or to bridge a transition before permanent placement.</li>
</ul>

<h2>"But I feel guilty"</h2>
<p>This is the single most common thing we hear. Here's the reframe: a caregiver who is rested, healthy, and not running on empty will care for their loved one <em>longer</em> and <em>better</em>. Respite isn't selfish — it's protective of the very relationship you're trying to honor.</p>

<h2>How to start without overthinking</h2>
<p>Try one weekend. Pick a member home in our locator that offers weekly or long-term respite. Drop them off Friday afternoon. Pick them up Sunday. See how it goes for both of you.</p>

<p>Or just call us: <strong>(586) 306-8449</strong>. We'll find you respite that fits this weekend, this month, or whenever you need to come up for air.</p>
    `,
  },
];

export function findPost(slug) {
  return POSTS.find((p) => p.slug === slug);
}
